import React, { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router';
import { motion } from 'framer-motion';
import { Calendar, Clock, MapPin, Users, ArrowLeft, Award, Loader2 } from 'lucide-react';
import { getWorkshop } from '../../api/client';
import { useToast } from '../../contexts/ToastContext';
import Button from '../../components/ui/Button';

interface WorkshopDetail {
  id: number;
  title: string;
  description: string;
  duration: string;
  start_date?: string;
  end_date?: string;
  start_time?: string;
  meeting_link?: string;
  price: number;
  max_seats?: number;
  seats_filled?: number;
  status: string;
  trainer_name?: string;
  trainer_avatar?: string;
}

export default function WorkshopDetailScreen(): React.JSX.Element {
  const { id } = useParams<{ id: string }>();
  const toast = useToast();
  const [workshop, setWorkshop] = useState<WorkshopDetail | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!id) return;
    (async () => {
      try {
        const res = await getWorkshop(parseInt(id));
        if (res.workshop) {
          setWorkshop(res.workshop);
        } else {
          toast.show('Workshop not found', 'error');
        }
      } catch (e: any) {
        toast.show(e.message || 'Failed to load workshop', 'error');
      } finally {
        setLoading(false);
      }
    })();
  }, [id]);

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <Loader2 className="animate-spin text-slate-400" size={32} />
      </div>
    );
  }

  if (!workshop) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <div className="text-center">
          <Calendar size={48} className="mx-auto text-slate-300 mb-4" />
          <h1 className="text-2xl font-bold text-slate-900 mb-2">Workshop Not Found</h1>
          <Link to="/workshops" className="text-amber-600 font-medium hover:underline">Browse all workshops</Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50">
      <div className="max-w-4xl mx-auto px-4 py-8">
        <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }}>
          <Link to="/workshops" className="inline-flex items-center gap-2 text-sm text-slate-500 hover:text-slate-900 transition-colors mb-6">
            <ArrowLeft size={16} /> Back to Workshops
          </Link>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
          className="bg-white rounded-3xl border border-slate-200 overflow-hidden"
        >
          {/* Header */}
          <div className="bg-slate-900 text-white p-8 sm:p-12">
            <div className="flex items-center gap-2 mb-4">
              <span className="px-3 py-1 rounded-full bg-emerald-500/20 text-emerald-300 text-xs font-bold uppercase tracking-wider">
                {workshop.status === 'upcoming' ? 'Upcoming' : 'Live'}
              </span>
              <span className="px-3 py-1 rounded-full bg-white/10 text-white/80 text-xs font-bold uppercase tracking-wider">
                {workshop.duration || '2 hours'}
              </span>
            </div>
            <h1 className="text-3xl sm:text-4xl font-bold mb-4">{workshop.title}</h1>
            <p className="text-slate-300 text-lg max-w-2xl">{workshop.description || 'Join this hands-on workshop led by industry experts.'}</p>
          </div>

          <div className="p-8 sm:p-12">
            <div className="grid sm:grid-cols-2 gap-8 mb-10">
              <div className="space-y-4">
                <h3 className="text-lg font-bold text-slate-900">Workshop Details</h3>
                {workshop.start_date && (
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-xl bg-amber-50 flex items-center justify-center">
                      <Calendar size={18} className="text-amber-600" />
                    </div>
                    <div>
                      <p className="text-xs text-slate-400 font-medium uppercase">Date</p>
                      <p className="text-sm font-bold text-slate-900">
                        {new Date(workshop.start_date).toLocaleDateString('en-IN', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' })}
                      </p>
                    </div>
                  </div>
                )}
                {workshop.start_time && (
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-xl bg-blue-50 flex items-center justify-center">
                      <Clock size={18} className="text-blue-600" />
                    </div>
                    <div>
                      <p className="text-xs text-slate-400 font-medium uppercase">Time</p>
                      <p className="text-sm font-bold text-slate-900">{workshop.start_time}</p>
                    </div>
                  </div>
                )}
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-xl bg-purple-50 flex items-center justify-center">
                    <MapPin size={18} className="text-purple-600" />
                  </div>
                  <div>
                    <p className="text-xs text-slate-400 font-medium uppercase">Format</p>
                    <p className="text-sm font-bold text-slate-900">Online Live Session</p>
                  </div>
                </div>
                {workshop.max_seats && (
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-xl bg-emerald-50 flex items-center justify-center">
                      <Users size={18} className="text-emerald-600" />
                    </div>
                    <div>
                      <p className="text-xs text-slate-400 font-medium uppercase">Seats</p>
                      <p className="text-sm font-bold text-slate-900">{workshop.seats_filled || 0} / {workshop.max_seats} filled</p>
                    </div>
                  </div>
                )}
              </div>

              <div className="bg-slate-50 rounded-2xl p-6 border border-slate-100">
                <h3 className="text-lg font-bold text-slate-900 mb-4">Register Now</h3>
                <div className="flex items-baseline gap-2 mb-4">
                  <span className="text-3xl font-bold text-slate-900">{workshop.price ? `₹${Number(workshop.price).toLocaleString('en-IN')}` : 'Free'}</span>
                </div>
                <Button size="lg" className="w-full mb-3">
                  {workshop.price ? 'Enroll Now' : 'Register for Free'}
                </Button>
                <p className="text-xs text-slate-500 text-center">
                  Limited seats. Secure your spot today.
                </p>
              </div>
            </div>

            {/* Trainer */}
            {workshop.trainer_name && (
              <div className="border-t border-slate-100 pt-8">
                <h3 className="text-lg font-bold text-slate-900 mb-4">Led By</h3>
                <div className="flex items-center gap-4">
                  {workshop.trainer_avatar ? (
                    <img src={workshop.trainer_avatar} alt={workshop.trainer_name} className="w-14 h-14 rounded-full object-cover" />
                  ) : (
                    <div className="w-14 h-14 rounded-full bg-amber-100 flex items-center justify-center text-amber-700 font-bold text-lg">
                      {workshop.trainer_name.charAt(0)}
                    </div>
                  )}
                  <div>
                    <p className="font-bold text-slate-900">{workshop.trainer_name}</p>
                    <p className="text-sm text-slate-500">Workshop Instructor</p>
                  </div>
                </div>
              </div>
            )}
          </div>
        </motion.div>
      </div>
    </div>
  );
}
